import { ErrorComponent, type ErrorComponentProps } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'

import { AccessDeniedState } from '@/components/shared/access-denied-state'
import { organizationStore, listOrganizationUsersQueryOptions } from '@/stores/organization.store'
import { getMeQueryOptions } from '@/stores/user.store'

/**
 * Thrown by `requirePermission` when the current user may not open a route.
 * Typed rather than a bare Error so the shared `errorComponent` can tell an
 * RBAC denial apart from a genuine load failure and keep the latter loud.
 */
export class PermissionDeniedRouteError extends Error {
  /** User-vocabulary domain noun for the denied state, e.g. "team settings". */
  readonly noun?: string

  constructor(noun?: string) {
    super('permission denied')
    this.name = 'PermissionDeniedRouteError'
    this.noun = noun
  }
}

/**
 * `beforeLoad` guard for permission-gated routes. Throws
 * `PermissionDeniedRouteError` unless the current user holds at least one of
 * `permission` in the currently selected organization.
 *
 * It deliberately does not redirect: the user clicked or typed a real URL
 * (a deep link, a stale bookmark, a link shared by a colleague with broader
 * access) and deserves an answer *at that URL*. Pair it with
 * `errorComponent: PermissionDeniedRoute` so the denial renders in place —
 * the location is preserved, refresh re-evaluates, and browser back works.
 *
 * Reuses the same ListOrganizationUsers-sourced `effective_permissions` set
 * as usePermissions (see its doc comment for why not GetMe), so a route
 * guard and an in-page check never disagree.
 *
 * Usage:
 * ```ts
 * beforeLoad: async ({ context }) => {
 *   await requirePermission(context.queryClient, 'TeamManage', 'team settings')
 * },
 * errorComponent: PermissionDeniedRoute,
 * ```
 */
export async function requirePermission(
  queryClient: QueryClient,
  permission: string | string[],
  noun?: string
): Promise<void> {
  const organizationId = organizationStore.state.selectedOrganizationId
  // No org selected yet (e.g. mid-onboarding, or _app's beforeLoad hasn't
  // resolved one) — that state owns its own redirect; nothing to check here.
  if (!organizationId) return

  const [me, orgUsersResponse] = await Promise.all([
    queryClient.ensureQueryData(getMeQueryOptions()),
    queryClient.ensureQueryData(listOrganizationUsersQueryOptions(organizationId)),
  ])

  const currentOrgUser = orgUsersResponse.users.find((orgUser) => orgUser.user?.id === me.user?.id)
  const effectivePermissions = new Set(currentOrgUser?.effectivePermissions ?? [])

  const required = Array.isArray(permission) ? permission : [permission]
  const hasAccess = required.some((perm) => effectivePermissions.has(perm))

  if (!hasAccess) {
    throw new PermissionDeniedRouteError(noun)
  }
}

/**
 * Shared route `errorComponent` for routes guarded by `requirePermission`.
 * Renders the page denied state for an RBAC denial and defers every other
 * error to the router's default error component, so a broken loader still
 * looks broken instead of masquerading as a permission problem.
 */
export function PermissionDeniedRoute({ error }: ErrorComponentProps) {
  if (error instanceof PermissionDeniedRouteError) {
    return <AccessDeniedState variant="page" noun={error.noun} />
  }
  return <ErrorComponent error={error} />
}
